import api from '@/services/api';
import type {
    CalendarConnectionInfo,
    CalendarProvider,
    ConnectionWithCalendars,
    SyncedCalendarEvent,
    BookingPage,
    BookingPageListItem,
    BookingPageFormData,
    BookingSlot,
    Booking,
    CohostSuggestion,
} from '../types/calendar.types';

export const calendarApi = {
    listConnections: () =>
        api.get<CalendarConnectionInfo[]>('/v2/calendar/connections'),

    getConnectionsWithCalendars: () =>
        api.get<ConnectionWithCalendars[]>('/v2/calendar/connections/calendars'),

    updateSelectedCalendars: (connectionId: string, selectedCalendarIds: string[]) =>
        api.patch(`/v2/calendar/connections/${connectionId}/calendars`, { selectedCalendarIds }),

    getConnectUrl: (provider: CalendarProvider, redirectUrl?: string) =>
        api.get<{ authUrl: string }>(`/v2/calendar/connect/${provider}`, {
            params: { redirectUrl },
        }),

    disconnect: (connectionId: string) =>
        api.delete(`/v2/calendar/connections/${connectionId}`),

    fetchEvents: (start: string, end: string) =>
        api.get<SyncedCalendarEvent[]>('/v2/calendar/events', {
            params: { start, end },
        }),

    syncAll: () =>
        api.post('/v2/calendar/sync/all'),

    updateEventTime: (eventId: string, start: string, end: string, sendUpdates: boolean) =>
        api.patch<SyncedCalendarEvent>(`/v2/calendar/events/${eventId}`, { start, end, sendUpdates }),

    // ── Booking Pages ──

    listBookingPages: (workspaceId: string) =>
        api.get<BookingPageListItem[]>('/v2/calendar/booking/pages', {
            params: { workspaceId },
        }),

    createBookingPage: (data: BookingPageFormData & { workspaceId: string }) =>
        api.post<BookingPage>('/v2/calendar/booking/pages', data),

    getBookingPage: (bookingPageId: string) =>
        api.get<BookingPage>(`/v2/calendar/booking/page/${bookingPageId}`),

    updateBookingPage: (bookingPageId: string, data: Partial<BookingPageFormData>) =>
        api.patch<BookingPage>(`/v2/calendar/booking/page/${bookingPageId}`, data),

    deleteBookingPage: (bookingPageId: string) =>
        api.delete(`/v2/calendar/booking/page/${bookingPageId}`),

    getCohostSuggestions: (workspaceId: string, query: string) =>
        api.get<CohostSuggestion[]>('/v2/calendar/booking/cohost-suggestions', {
            params: { workspaceId, q: query },
        }),

    generatePrivateLink: (bookingPageId: string, data: { candidateId?: string; candidateEmail: string; candidateName: string; assessmentId?: string }) =>
        api.post<{ token: string; bookingPageId: string }>(`/v2/calendar/booking/page/${bookingPageId}/private-link`, data),

    cancelBooking: (bookingId: string) =>
        api.post(`/v2/calendar/booking/${bookingId}/cancel`),

    getBookingsForCandidate: (candidateId: string) =>
        api.get<Booking[]>(`/v2/calendar/booking/candidate/${candidateId}`),

    listBookings: (params?: { workspaceId?: string; status?: string; limit?: number; skip?: number }) =>
        api.get<Booking[]>('/v2/calendar/booking/', { params }),

    getFreeSlotsForInsert: (data: { calendarIds: string[]; startDate: string; endDate: string; duration?: number; timezone?: string }) =>
        api.post('/v2/calendar/booking/free-slots', data),

    sendCustomEmail: (data: { to: string; subject: string; bodyHtml: string }) =>
        api.post<{ sent: boolean }>('/v2/calendar/booking/send-custom-email', data),

    sendBookingInvitation: (data: {
        bookingPageId: string;
        applicationId?: string;
        candidateId?: string;
        candidateEmail?: string;
        candidateName?: string;
        customMessage?: string;
        assessmentId?: string;
    }) =>
        api.post<{ sent: boolean; bookingPageId: string }>(
            '/v2/calendar/booking/send-invitation-email',
            data,
        ),

    deleteEvent: (eventId: string) =>
        api.delete(`/v2/calendar/events/${eventId}`),

    // ── Event creation ──

    createEvent: (data: {
        connectionId: string;
        calendarId: string;
        title: string;
        startDateTime: string;
        endDateTime: string;
        timezone: string;
        attendees?: { email: string; name?: string }[];
        meetingUrl?: string;
        location?: string;
        description?: string;
    }) =>
        api.post<SyncedCalendarEvent>('/v2/calendar/events', data),

    searchCandidates: (workspaceId: string, query: string) =>
        api.get<{ name: string; email: string }[]>('/v2/calendar/candidates/search', {
            params: { workspaceId, q: query },
        }),
};
